import React, {useEffect, useRef, useState} from "react";
import Button from "@source/components/UI/buttons/button/button";
import FileInput from "@source/components/UI/inputs/fileInput/fileInput";
import ConfirmModal from "@source/components/confirmModal/confirmModal";
import Notification from "@source/components/snackMessage/notification";
import iconPictureFileInput from "@source/assets/icons/iconPictureFileInput.svg";
import ConsentController from "@source/api/controllers/consentController";
import {ConsentCategory, ConsentSupportingDocument} from "@source/api/models/consentModels";
import {
  MAX_CONSENT_SUPPORTING_DOCUMENTS,
  formatConsentFileSize,
  getSupportingDocumentErrorMessage,
  isConsentPdfHeader,
  mergeSupportingDocuments,
  selectSupportingDocumentFiles,
  shouldRefreshSupportingDocuments,
} from "@source/api/models/consentHelpers";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import styles from "./categoryLibrary.module.scss";

type UploadStage = "preparing" | "uploading" | "confirming" | "error";

interface UploadProgress {
  key: string;
  name: string;
  stage: UploadStage;
  message?: string;
}

const stageLabel: Record<Exclude<UploadStage, "error">, string> = {
  preparing: "Preparing upload",
  uploading: "Uploading",
  confirming: "Confirming",
};

const pdfHeaderIsValid = async (file: File) => {
  const header = await file.slice(0, 5).arrayBuffer();
  return isConsentPdfHeader(new Uint8Array(header));
};

const selectedFiles = (value: File | File[] | null): File[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export const SupportingDocuments = ({organizationId, category, canManage}: {
  organizationId: string;
  category?: ConsentCategory;
  canManage: boolean;
}) => {
  const [documents, setDocuments] = useState<ConsentSupportingDocument[]>(category?.supportingDocuments ?? []);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [inputKey, setInputKey] = useState(0);
  const [message, setMessage] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [busy, setBusy] = useState(false);
  const uploadLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (!uploadLock.current) setDocuments(category?.supportingDocuments ?? []);
  }, [category?._id, category?.supportingDocuments]);

  if (!category) {
    return <section className={styles.sectionCard}>
      <h3>Supporting documents</h3>
      <p>Supporting documents can be added after this Consent Category is created.</p>
    </section>;
  }

  const atLimit = documents.length >= MAX_CONSENT_SUPPORTING_DOCUMENTS;
  const refreshDocuments = async () => {
    const fresh = await ConsentController.getCategory(organizationId, category._id);
    if (mounted.current) setDocuments(fresh.supportingDocuments ?? []);
  };
  const uploadFiles = async (incoming: File[]) => {
    if (!canManage || uploadLock.current || !incoming.length) return;
    const inFlight = uploads.filter((item) => item.stage !== "error").length;
    const selection = selectSupportingDocumentFiles(incoming, documents.length + inFlight);
    setMessage(selection.errors.join(" "));
    if (!selection.accepted.length) return;
    uploadLock.current = true;
    setBusy(true);
    const batch = selection.accepted.map((file, index) => ({
      key: `${index}-${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      stage: "preparing" as const,
      file,
    }));
    setUploads((current) => [
      ...current.filter((item) => item.stage !== "error"),
      ...batch.map(({key, name, stage}) => ({key, name, stage})),
    ]);
    let succeeded = 0;
    try {
      await Promise.all(batch.map(async (item) => {
        const fail = (error: unknown, fallback: string) => {
          if (!mounted.current) return;
          setUploads((current) => current.map((entry) => entry.key === item.key
            ? {...entry, stage: "error", message: getSupportingDocumentErrorMessage(error, fallback)}
            : entry));
        };
        const setStage = (stage: Exclude<UploadStage, "error">) => {
          if (!mounted.current) return;
          setUploads((current) => current.map((entry) => entry.key === item.key ? {...entry, stage} : entry));
        };
        try {
          if (!(await pdfHeaderIsValid(item.file))) {
            fail(new Error("The selected file does not have a valid PDF header."), "The selected file does not have a valid PDF header.");
            return;
          }
          const target = await ConsentController.createSupportingDocumentUploadUrl(organizationId, category._id);
          setStage("uploading");
          await ConsentController.uploadPdf(target.uploadUrl, item.file);
          setStage("confirming");
          const updated = await ConsentController.confirmSupportingDocument(organizationId, category._id, {
            objectKey: target.objectKey,
            originalFileName: item.file.name,
            mimeType: "application/pdf",
          });
          if (!mounted.current) return;
          setDocuments((current) => mergeSupportingDocuments(current, updated.supportingDocuments));
          setUploads((current) => current.filter((entry) => entry.key !== item.key));
          succeeded += 1;
        } catch (error: unknown) {
          fail(error, "Unable to upload this PDF.");
        }
      }));
      try {
        await refreshDocuments();
      } catch {
        // Confirmed responses already updated the list.
      }
      if (mounted.current && succeeded) {
        new Notification().success(succeeded === 1 ? "Supporting document uploaded." : "Supporting documents uploaded.");
      }
    } finally {
      uploadLock.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const removeDocument = async (supportingDocumentId: string) => {
    setRemovingId(supportingDocumentId);
    setMessage("");
    try {
      const updated = await ConsentController.deleteSupportingDocument(organizationId, category._id, supportingDocumentId);
      if (!mounted.current) return;
      setDocuments(updated.supportingDocuments ?? []);
      new Notification().success("Supporting document removed.");
    } catch (error: unknown) {
      if (!mounted.current) return;
      setMessage(getSupportingDocumentErrorMessage(error, "Unable to remove this document."));
      if (shouldRefreshSupportingDocuments(error)) {
        try { await refreshDocuments(); } catch { /* keep the current list */ }
      }
    } finally {
      if (mounted.current) setRemovingId("");
    }
  };
  const chooseFiles = (value: File | File[] | null) => {
    const files = selectedFiles(value);
    setInputKey((key) => key + 1);
    void uploadFiles(files);
  };

  return <section className={styles.sectionCard} aria-label="Supporting documents">
    <div className={styles.sectionHeading}>
      <h3>Supporting documents</h3>
      <span className={styles.supportingCount}>{documents.length} / {MAX_CONSENT_SUPPORTING_DOCUMENTS}</span>
    </div>
    <p>Family members will see these documents before signing. Changes apply to consents sent from now on.</p>
    {documents.length ? <ul className={styles.documentList}>
      {documents.map((document) => <li className={styles.documentRow} key={document._id}>
        <div className={styles.documentMeta}>
          <strong>{document.originalFileName}</strong>
          <span>{formatConsentFileSize(document.size)} · Uploaded {formatConsentDate(document.uploadedAt)}</span>
        </div>
        <div className={styles.documentActions}>
          <Button size="small" variant="outlined" disabled={!document.url} onClick={() => {
            if (document.url) window.open(document.url, "_blank", "noopener,noreferrer");
          }}>View</Button>
          {canManage ? <ConfirmModal title="Remove this document?" subTitle="Consents already sent will still include it." confirmCb={() => removeDocument(document._id)} confirmLoading={removingId === document._id} confirmBtnInner="Remove" rejectBtnInner="Cancel">
            <Button size="small" variant="outlined" disabled={busy || Boolean(removingId)}>Remove</Button>
          </ConfirmModal> : null}
        </div>
      </li>)}
    </ul> : <p>No supporting documents yet.</p>}
    {uploads.length ? <ul className={styles.uploadProgress} aria-live="polite">
      {uploads.map((item) => <li className={item.stage === "error" ? styles.uploadError : undefined} key={item.key}>
        {item.name}: {item.stage === "error" ? item.message || "Upload failed" : stageLabel[item.stage]}
      </li>)}
    </ul> : null}
    {message ? <p className={styles.formError} role="alert">{message}</p> : null}
    {canManage ? <div className={styles.supportingUpload}><div className={styles.photo}>
      <FileInput
        key={inputKey}
        className={styles.img}
        btnClassName={styles.imgBtn}
        areaTextClassName={styles.areaText}
        subAreaTextClassName={styles.subAreaText}
        selectedFile={null}
        setSelectedFile={chooseFiles}
        multiple
        loading={busy}
        disabled={busy || atLimit || Boolean(removingId)}
        acceptFormats="application/pdf,.pdf"
        uploadImg={iconPictureFileInput}
        imgColor="#7E808F"
        imgWidth={40}
        imgHeight={40}
        areaText="Drop a PDF here, or"
        subAreaText={atLimit ? "A category can have at most 10 supporting documents." : "PDF only · maximum 25 MiB"}
      />
    </div></div> : null}
  </section>;
};
